import { PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import { onPartLeadTimeChange } from '../calculations/recalculate';
import { getQuoteLineOrThrow } from '../utilities/action-guards';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue } from '../utilities/issue-managers';
import { QuoteLineParams } from './types';

/**
 * Copies the first quantity break's lead time onto every other break.
 *
 * Quantity-aware lines calculate a lead time per break, and a part with no
 * routing produces none at all — leaving the user to type the same value into
 * each break to clear the per-break validation. This applies it once.
 *
 * Each updated break is recorded as a user override, the same as typing the
 * value in, so the line's suggestion status reflects that these were set by
 * hand.
 */
export function leadTimeAppliedToAllBreaks(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuoteLineParams>,
) {
  const { quoteLineUuid } = action.payload;
  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  const [firstQuotePriceUuid, ...remainingQuotePriceUuids] =
    quoteLine.quotePriceUuids;

  if (!firstQuotePriceUuid) {
    return;
  }

  const leadTimeWeeks =
    quoteLine.quotePrices[firstQuotePriceUuid]?.leadTimeWeeks;

  // Nothing to copy. The button is disabled in this state, so this only guards
  // against the value being cleared between render and dispatch.
  if (leadTimeWeeks == null) {
    return;
  }

  remainingQuotePriceUuids.forEach((quotePriceUuid) => {
    onPartLeadTimeChange(state, {
      quoteLineUuid,
      quotePriceUuid,
      leadTimeWeeks,
    });

    // The value came from a break that already validated, so any outstanding
    // "invalid lead time" issue on this break no longer applies.
    deleteIssue(
      state,
      buildIssueId({
        area: 'quotePrice',
        field: 'leadTimeWeeks',
        quoteLineUuid,
        quotePriceUuid,
        type: 'invalid',
      }),
    );
  });
}
